/* eslint-disable react-hooks/exhaustive-deps */
import {
    Badge,
    Box,
    FormErrorMessage,
    FormHelperText,
    HStack,
    IconButton,
    InputGroup,
    InputRightElement,
    Modal,
    ModalOverlay,
    Skeleton,
    Stack,
    Tooltip,
    useDisclosure,
} from '@chakra-ui/react';
import {
    FormControl,
    FormLabel,
    Input,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
} from 'components/v4';
import ChakraButton from 'components/v4/Button';
import ChipInput from 'components/v4/ChipInput/ChipInput';
import { ChipList } from 'components/v4/ChipInput/ChipList';
import { useTenant } from './useTenant';
import { useEmail } from './useEmail';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDarkToggleBtnStyles } from 'pages/telephony/numberBlocks/split-join/constants';
import { axios } from '@/services/axios';
import { toast } from 'react-toastify';
import TenantLookup from './TenantLookup';
import { QuestionIcon } from '@chakra-ui/icons';
import walkthroughIds from '../../../accounts/admins/walkthroughIds';

export default function EditMicrosoftSSO({ isOpen, onClose, providerId }) {
    const {
        isOpen: isOpenLookup,
        onOpen: onOpenLookup,
        onClose: onCloseLookup,
    } = useDisclosure();

    const { darkMode } = useSelector((state) => state.settings);
    const darkToggleBtnStyle = getDarkToggleBtnStyles(darkMode);

    const [isLoading, setIsLoading] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const {
        ufTenantInput,
        ufEmailInput,
        ufOptionBtn,
        ufLookupBtn,
        ufBackBtn,
        ufSubmitBtn,
    } = walkthroughIds.adminSSO;

    const initialState = {
        isBoth: '0',
        emailDomains: [],
        tenantIds: [],
    };

    const [providerState, setProviderState] = useState(initialState);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isUploading, setIsUploading] = useState(false);

    const {
        tenants,
        setTenants,
        tenantInput,
        tenantError,
        setTenantError,
        addTenants,
        handleTenantChange,
        handleTenantKeyDown,
        handleTenantPaste,
        handleTenantCloseClick,
    } = useTenant(setProviderState);

    const {
        emails,
        setEmails,
        emailInput,
        emailError,
        setEmailError,
        handleEmailChange,
        handleEmailKeyDown,
        handleEmailPaste,
        handleEmailCloseClick,
    } = useEmail(setProviderState);

    //TODO: Refactor to use a call back
    useEffect(() => {
        async function fetchData() {
            if (isOpen) {
                setIsLoading(true);
                try {
                    const res = await axios.get(`/sso/provider/${providerId}`);
                    setProviderState({
                        ...res.data,
                        isBoth: res.data?.isBoth == true ? '1' : '0',
                    });
                    setTenants(() => [
                        ...(res.data?.tenantIds?.[0] === ''
                            ? []
                            : res.data?.tenantIds || []),
                    ]);
                    setEmails(() => [
                        ...(res.data?.emailDomains?.[0] === ''
                            ? []
                            : res.data?.emailDomains || []),
                    ]);
                } catch (e) {
                    toast.error(e?.response?.data?.message);
                } finally {
                    setIsLoading(false);
                }
            }
        }
        fetchData();
    }, [isOpen]);

    useEffect(() => {
        const isOrCondition =
            providerState?.isBoth === '0' &&
            (tenants.length > 0 || emails?.length > 0);
        const isAndCondition =
            providerState?.isBoth === '1' &&
            tenants.length > 0 &&
            emails.length > 0;
        setIsDisabled(!(isOrCondition || isAndCondition));
    }, [tenants, emails]);

    const handleUpdateProvider = async () => {
        try {
            var request = {
                ...providerState,
                isBoth: providerState.isBoth == '1' ? true : false,
                tenantIds: tenants,
                emailDomains: emails,
            };
            setIsUploading(true);
            await axios.put('/sso/provider', request);
            toast.success('Provider has been saved successfully!');
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleOpenLookup = () => {
        setTenantError({ isError: false, message: '', duplicates: '' });
        onOpenLookup();
    };

    const handleCloseModal = () => {
        setProviderState(initialState);
        setTenants([]);
        setEmails([]);
        setEmailError({ isError: false, message: '', duplicates: '' });
        setTenantError({ isError: false, message: '', duplicates: '' });
        onClose();
    };

    return (
        <Modal motionPreset="slideInTop" isOpen={isOpen} autoFocus={false}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <HStack>
                        <span>Edit SSO for Microsoft</span>
                        {isLoading ? (
                            <Skeleton h={5} w={'15%'} />
                        ) : (
                            <Badge
                                colorScheme={
                                    providerState?.isVerified
                                        ? 'green'
                                        : 'orange'
                                }
                                variant={'subtle'}>
                                {providerState?.isVerified
                                    ? 'Verified'
                                    : 'Pending'}
                            </Badge>
                        )}
                    </HStack>
                </ModalHeader>
                <ModalBody>
                    {isLoading ? (
                        <Stack>
                            <Skeleton w={'30%'} h={5} />
                            <Skeleton h={8} />
                            <Skeleton h={10} />
                            <Skeleton h={5} w={'75%'} />
                            <Skeleton w={'20%'} h={5} />
                            <HStack width={'30%'} spacing={0.5}>
                                <Skeleton flex={1} h={12} />
                                <Skeleton flex={1} h={12} />
                            </HStack>
                            <Skeleton w={'35%'} h={5} />
                            <Skeleton h={8} />
                            <Skeleton h={10} />
                            <Skeleton h={5} w={'35%'} />
                        </Stack>
                    ) : (
                        <>
                            <FormControl
                                isRequired={providerState?.isBoth == 1}
                                mb={2}
                                isInvalid={tenantError.isError}>
                                <FormLabel>Tenant IDs</FormLabel>
                                <ChipList
                                    inputs={tenants}
                                    onCloseClick={handleTenantCloseClick}
                                />
                                <InputGroup
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}>
                                    <Input
                                        data-walkthroughid={ufTenantInput}
                                        placeholder="Enter your Tenant IDs"
                                        onPaste={handleTenantPaste}
                                        onKeyDown={handleTenantKeyDown}
                                        onChange={handleTenantChange}
                                        value={tenantInput.trim()}
                                    />

                                    {isHovered && (
                                        <InputRightElement>
                                            <Tooltip
                                                label={
                                                    'Click me! Lookup your Tenant ID'
                                                }
                                                hasArrow
                                                placement="bottom">
                                                <IconButton
                                                    data-walkthroughid={
                                                        ufLookupBtn
                                                    }
                                                    color={
                                                        darkMode
                                                            ? 'white'
                                                            : 'inherit'
                                                    }
                                                    bg={'none'}
                                                    _active={{ bg: 'none' }}
                                                    _hover={{
                                                        bg: 'none',
                                                        color: darkMode
                                                            ? 'white'
                                                            : 'inherit',
                                                    }}
                                                    onClick={() => {
                                                        handleOpenLookup();
                                                    }}
                                                    className="btn"
                                                    icon={<QuestionIcon />}
                                                />
                                            </Tooltip>
                                        </InputRightElement>
                                    )}
                                </InputGroup>

                                {!tenantError.isError ? (
                                    <FormHelperText>
                                        eg. 6b29fc40-ca47-1067-b31d-00dd010662da
                                    </FormHelperText>
                                ) : tenantError.message ? (
                                    <FormErrorMessage>
                                        {tenantError.message}
                                    </FormErrorMessage>
                                ) : (
                                    <FormErrorMessage>
                                        {tenantError.duplicates}
                                    </FormErrorMessage>
                                )}
                            </FormControl>

                            <FormControl
                                mb={2}
                                sx={darkToggleBtnStyle}
                                isRequired>
                                <FormLabel>Option</FormLabel>
                                <ToggleButtonGroup
                                    data-walkthroughid={ufOptionBtn}
                                    className="toggleButton"
                                    color="primary"
                                    exclusive
                                    value={providerState?.isBoth}
                                    aria-label="Platform"
                                    style={{
                                        height: '48px',
                                        alignItems: 'center',
                                    }}
                                    onChange={(e) => {
                                        setProviderState((prevState) => ({
                                            ...prevState,
                                            isBoth: e.target.value,
                                        }));
                                    }}>
                                    <ToggleButton
                                        size="medium"
                                        style={{
                                            width: '60px',
                                        }}
                                        value="0">
                                        OR
                                    </ToggleButton>
                                    <ToggleButton
                                        disabled={
                                            !emails.length > 0 ||
                                            !tenants.length > 0
                                        }
                                        size="medium"
                                        style={{
                                            width: '60px',
                                            // backgroundColor:
                                            //     (!emails?.length > 0 ||
                                            //     !tenants?.length > 0)
                                            //         ? "#6c757d"
                                            //         : null,
                                        }}
                                        value="1">
                                        AND
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {providerState?.isBoth && (
                                    <FormHelperText fontWeight={'bold'}>
                                        You have selected{' '}
                                        {providerState?.isBoth == 0
                                            ? 'OR'
                                            : 'AND'}
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <FormControl
                                isRequired={providerState?.isBoth == 1}
                                mb={2}
                                isInvalid={emailError.isError}>
                                <FormLabel>Email Domains</FormLabel>
                                <ChipList
                                    inputs={emails}
                                    onCloseClick={handleEmailCloseClick}
                                />
                                <ChipInput
                                    data-walkthroughid={ufEmailInput}
                                    placeholder="Enter your Email Domains"
                                    onPaste={handleEmailPaste}
                                    onKeyDown={handleEmailKeyDown}
                                    onChange={handleEmailChange}
                                    value={emailInput.trim()}
                                />
                                {!emailError.isError ? (
                                    <FormHelperText>
                                        eg. yourdomain.com
                                    </FormHelperText>
                                ) : emailError.message ? (
                                    <FormErrorMessage>
                                        {emailError.message}
                                    </FormErrorMessage>
                                ) : (
                                    <FormErrorMessage>
                                        {emailError.duplicates}
                                    </FormErrorMessage>
                                )}
                            </FormControl>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Box
                        style={{
                            border: 'none',
                            textAlign: 'center',
                            justifyContent: 'center',
                        }}>
                        <ChakraButton
                            data-walkthroughid={ufBackBtn}
                            variant="secondary"
                            className="btn btn-secondary btn-sm disabled:cursor-not-allowed"
                            onClick={() => {
                                handleCloseModal();
                            }}>
                            Close
                        </ChakraButton>
                    </Box>
                    <Box
                        style={{
                            border: 'none',
                            textAlign: 'center',
                            justifyContent: 'center',
                            marginLeft: 10,
                        }}>
                        <ChakraButton
                            data-walkthroughid={ufSubmitBtn}
                            type="submit"
                            isDisabled={isDisabled}
                            isLoading={isUploading}
                            className="btn-sm disabled:cursor-not-allowed"
                            onClick={() => handleUpdateProvider()}>
                            Submit
                        </ChakraButton>
                    </Box>
                </ModalFooter>
            </ModalContent>

            <TenantLookup
                isOpenLookup={isOpenLookup}
                tenants={tenants}
                addTenants={addTenants}
                setTenantError={setTenantError}
                onCloseLookup={onCloseLookup}
            />
        </Modal>
    );
}
